/**
 * LoyaltyTab — the customer's points & rewards, laid out the details-page way:
 * a summary sidebar (member card + balance/tier/program + adjust-points) beside
 * section cards for the logs (point history, claimed rewards). Reuses the same
 * rewards hooks + presentation maps as /rewards (never rebuilds the data layer),
 * but renders the section-card pattern (SectionHeader + one card of borderless
 * divider rows) instead of the boxed drill-down used inside the rewards list.
 */

import { Fragment, useState } from "react";
import { Award, Pencil, Plus, Minus, Loader2, ChevronLeft, ChevronRight, Copy, Check, Star, Gift, Clock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCustomerPointHistory,
  useCustomerClaimedRewards,
  useAwardBonusPoints,
  useRewardsConfig,
  type PointHistoryEntry,
  type ClaimedRewardItem,
} from "@/features/rewards/api/rewardsAdminHooks";
import { useCustomerOrders } from "@/features/customers/api/customerHooks";
import { REWARD_TYPE_CONFIG } from "@/features/rewards/components/MemberDetail";
import { pointsIcon, dayLabel } from "./ActivityTimelineCard";
import { CardTitle, timeAgo, formatMoney } from "./customerDetailPrimitives";
import { MemberCardForTier } from "./MemberCardForTier";

interface TierProgress {
  currentName: string;
  nextName: string;
  pct: number;
  remaining: number;
  maxed: boolean;
}

interface LifetimeStats {
  earned: number;
  redeemed: number;
  expired: number;
}

/** Sum grand_total of non-cancelled orders in the last 12 months. */
function spendLast12m(orders: Array<Record<string, any>>): number {
  const cutoff = Date.now() - 365 * 86_400_000;
  return orders.reduce((sum, o) => {
    if (o.status === "Cancelled") return sum;
    const t = Date.parse(String(o.transaction_date || "").replace(" ", "T"));
    if (Number.isNaN(t) || t < cutoff) return sum;
    return sum + (Number(o.grand_total) || 0);
  }, 0);
}

/** Lifetime earned / redeemed / expired from the point entries. */
function lifetimeStats(entries: PointHistoryEntry[]): LifetimeStats {
  let earned = 0,
    redeemed = 0,
    expired = 0;
  for (const e of entries) {
    const p = Number(e.points) || 0;
    if (p > 0) earned += p;
    else if (/expired/i.test(e.earn_source)) expired += -p;
    else redeemed += -p;
  }
  return { earned, redeemed, expired };
}

/** Points expiring within `days` (best-effort: soonest future-expiry batch, capped at balance). */
function expiringSoon(entries: PointHistoryEntry[], balance: number, days = 60): { points: number; date: string } | null {
  const now = Date.now();
  const horizon = now + days * 86_400_000;
  const soon = entries
    .filter((e) => e.points > 0 && e.expiry_date)
    .map((e) => ({ points: e.points, t: Date.parse(String(e.expiry_date).replace(" ", "T")), date: e.expiry_date as string }))
    .filter((e) => !Number.isNaN(e.t) && e.t > now && e.t <= horizon)
    .sort((a, b) => a.t - b.t);
  if (!soon.length || balance <= 0) return null;
  const points = Math.min(soon.reduce((s, e) => s + e.points, 0), balance);
  return points > 0 ? { points, date: soon[0].date } : null;
}

/** Progress to the next tier by min_spent, given the customer's 12-month spend. */
function tierProgress(
  tiers: Array<{ tier_name: string; min_spent: number }> | undefined,
  currentTier: string | undefined,
  spend: number,
): TierProgress | null {
  if (!tiers?.length) return null;
  const sorted = [...tiers].sort((a, b) => a.min_spent - b.min_spent);
  const curIdx = currentTier ? sorted.findIndex((t) => t.tier_name === currentTier) : -1;
  const cur = curIdx >= 0 ? sorted[curIdx] : sorted[0];
  const next = sorted[(curIdx >= 0 ? curIdx : 0) + 1];
  if (!next) return { currentName: cur.tier_name, nextName: "", pct: 1, remaining: 0, maxed: true };
  // Fill from zero toward the next threshold — intuitive + robust to tier/recent-spend mismatches.
  const pct = next.min_spent > 0 ? Math.max(0, Math.min(1, spend / next.min_spent)) : 0;
  return {
    currentName: cur.tier_name,
    nextName: next.tier_name,
    pct,
    remaining: Math.max(0, next.min_spent - spend),
    maxed: false,
  };
}

const HISTORY_PER_PAGE = 8;

interface LoyaltyTabProps {
  customer: string;
  customerName?: string;
  tier?: string;
  program?: string;
  storefrontKey?: string;
}

/** Group point entries by day, preserving their (newest-first) order. */
function groupEntriesByDay(entries: PointHistoryEntry[]): Array<[string, PointHistoryEntry[]]> {
  const groups: Array<[string, PointHistoryEntry[]]> = [];
  for (const e of entries) {
    const label = dayLabel(e.posting_date || "");
    const last = groups[groups.length - 1];
    if (last && last[0] === label) last[1].push(e);
    else groups.push([label, [e]]);
  }
  return groups;
}

/**
 * One point-history entry as a timeline node — same vertical-rail design as the
 * Activity card (icon node + connecting line + title/time/subtitle) for
 * consistency across the customer page.
 */
function PointTimelineRow({
  entry,
  isFirst,
  isLast,
}: {
  entry: PointHistoryEntry;
  isFirst: boolean;
  isLast: boolean;
}) {
  const Icon = pointsIcon(entry.earn_source || "");
  const positive = entry.points > 0;
  return (
    <div className="px-0">
      <div className="flex items-start gap-3.5">
        {/* Node column with the connecting rail */}
        <div className="relative flex w-9 shrink-0 justify-center">
          <span
            className={cn(
              "absolute left-1/2 w-px -translate-x-1/2 bg-gray-200 dark:bg-gray-700",
              isFirst ? "top-[18px]" : "top-0",
              isLast ? "h-[18px]" : "bottom-0",
            )}
          />
          <span className="relative z-10">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Icon className="h-4 w-4" />
            </span>
          </span>
        </div>
        <div className={cn("min-w-0 flex-1", isLast ? "pb-0" : "pb-4")}>
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium text-foreground">{entry.earn_source || "Loyalty"}</span>
            <span className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">
              {timeAgo(entry.posting_date || "")}
            </span>
          </div>
          <div className="truncate text-xs text-muted-foreground">
            <span
              className={cn(
                "font-mono font-semibold",
                positive ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400",
              )}
            >
              {positive ? "+" : ""}
              {entry.points.toLocaleString()}
            </span>
            {entry.description || entry.invoice ? ` · ${entry.description || entry.invoice}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Claim status → a Thai "is it still usable?" badge. */
function claimStatusBadge(status: string): { label: string; cls: string } {
  switch (status) {
    case "Claimed":
      return { label: "พร้อมใช้", cls: "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300" };
    case "Fulfilled":
      return { label: "ใช้แล้ว", cls: "bg-muted text-muted-foreground" };
    case "Cancelled":
      return { label: "ยกเลิก", cls: "bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-300" };
    default:
      return { label: status, cls: "bg-muted text-muted-foreground" };
  }
}

/** Free-item delivery state → Thai label. */
function deliveryLabel(status?: string | null): string | null {
  if (!status) return null;
  if (/fully delivered/i.test(status)) return "จัดส่งแล้ว";
  if (/partly/i.test(status)) return "จัดส่งบางส่วน";
  return "รอจัดส่ง";
}

/** The coupon the customer holds — tap to copy the code. */
function CouponCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code)?.then(() => {
      setCopied(true);
      toast.success("คัดลอกโค้ดแล้ว");
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      title="คัดลอกโค้ด"
      className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground/80 transition-colors hover:bg-muted/70"
    >
      {code}
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3 opacity-60" />}
    </button>
  );
}

/** One redeemed reward the customer holds — with its code + usability status. */
function ClaimRow({ claim }: { claim: ClaimedRewardItem }) {
  const config = REWARD_TYPE_CONFIG[claim.reward_type] ?? REWARD_TYPE_CONFIG.custom;
  const Icon = config.icon;
  const st = claimStatusBadge(claim.status);
  const delivery = claim.reward_type === "free_item" ? deliveryLabel(claim.delivery_status) : null;
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className={cn("shrink-0 rounded-full bg-muted p-1.5", config.color)}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-[13px] font-medium">{claim.reward_title}</p>
            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              {config.label}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            <span>{claim.claimed_at?.slice(0, 10) ?? "-"}</span>
            {claim.coupon_code && <CouponCode code={claim.coupon_code} />}
            {delivery && <span>• {delivery}</span>}
            {claim.sales_order && <span>• ใช้กับ {claim.sales_order}</span>}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", st.cls)}>{st.label}</span>
        <span className="rounded-full bg-orange-100 px-2 py-0.5 font-mono text-[12px] font-medium text-orange-700 dark:bg-orange-950/40 dark:text-orange-300">
          -{claim.points_spent.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

/** Sidebar summary: balance, tier, program + inline adjust-points action. */
function PointsSummaryCard({
  customer,
  balance,
  tier,
  program,
  progress,
  lifetime,
  pointsValue,
  currency = "THB",
}: {
  customer: string;
  balance: number;
  tier?: string;
  program?: string;
  progress?: TierProgress | null;
  lifetime?: LifetimeStats;
  /** Redemption value of the balance in base currency (balance × conversion_factor). */
  pointsValue?: number;
  currency?: string;
}) {
  const bonusMutation = useAwardBonusPoints();
  const [showBonus, setShowBonus] = useState(false);
  const [points, setPoints] = useState(0);
  const [reason, setReason] = useState("");
  const [isDeduct, setIsDeduct] = useState(false);

  const submit = () => {
    if (points <= 0) return toast.error("กรุณาระบุจำนวนแต้ม");
    if (!reason.trim()) return toast.error("กรุณาระบุเหตุผล");
    bonusMutation.mutate(
      { customer, points: isDeduct ? -points : points, reason },
      {
        onSuccess: () => {
          toast.success(isDeduct ? `หักแต้ม ${points} แต้มสำเร็จ` : `ให้แต้มโบนัส ${points} แต้มสำเร็จ`);
          setShowBonus(false);
          setPoints(0);
          setReason("");
        },
        onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
      },
    );
  };

  return (
    <div className={CARD}>
      <div className="p-5">
        {/* Important info + primary action — horizontal, leads the column. */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                คะแนนคงเหลือ
              </div>
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <span className="text-[30px] font-bold leading-tight tracking-tight text-foreground">
                  {balance.toLocaleString()}
                </span>
                <span className="text-[13px] text-muted-foreground">แต้ม</span>
              </div>
              {pointsValue != null && pointsValue > 0 && (
                <div className="text-[12px] text-muted-foreground">≈ {formatMoney(pointsValue, currency)}</div>
              )}
            </div>
            <div className="border-l border-border/40 pl-8">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                เทียร์
              </div>
              <div className="mt-1.5 text-[18px] font-semibold text-foreground">{tier || "-"}</div>
            </div>
            {program && (
              <div className="border-l border-border/40 pl-8">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  โปรแกรม
                </div>
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-[14px] font-medium text-foreground">
                  <Award className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="max-w-[200px] truncate">{program}</span>
                </div>
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowBonus((v) => !v)}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            ปรับแต้ม
          </Button>
        </div>

        {showBonus && (
          <div className="mt-4 space-y-3 rounded-lg border border-sky-200 bg-sky-50/50 p-3 dark:border-sky-900/40 dark:bg-sky-950/20">
            <div className="flex items-center gap-2">
              <Button variant={!isDeduct ? "default" : "outline"} size="sm" onClick={() => setIsDeduct(false)}>
                <Plus className="mr-1 h-3 w-3" />
                ให้แต้ม
              </Button>
              <Button variant={isDeduct ? "destructive" : "outline"} size="sm" onClick={() => setIsDeduct(true)}>
                <Minus className="mr-1 h-3 w-3" />
                หักแต้ม
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">จำนวนแต้ม</Label>
                <Input
                  type="number"
                  min={1}
                  value={points || ""}
                  onChange={(e) => setPoints(Math.max(0, Number(e.target.value) || 0))}
                  placeholder="100"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">เหตุผล</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="เช่น ชดเชยปัญหาออเดอร์"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowBonus(false)}>
                ยกเลิก
              </Button>
              <Button
                size="sm"
                onClick={submit}
                disabled={bonusMutation.isPending}
                variant={isDeduct ? "destructive" : "default"}
              >
                {bonusMutation.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                {isDeduct ? "หักแต้ม" : "ให้แต้มโบนัส"}
              </Button>
            </div>
          </div>
        )}

        {(progress || lifetime) && (
          <div className="mt-4 space-y-3 border-t border-border/40 pt-4">
            {progress && (
              <div>
                <div className="mb-1.5 flex items-center justify-between text-[12px]">
                  <span className="font-medium text-foreground">
                    {progress.maxed ? progress.currentName : `${progress.currentName} → ${progress.nextName}`}
                  </span>
                  <span className="text-muted-foreground">
                    {progress.maxed ? "เทียร์สูงสุดแล้ว" : `อีก ${formatMoney(progress.remaining, currency)}`}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${Math.round(progress.pct * 100)}%` }}
                  />
                </div>
              </div>
            )}
            {lifetime && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
                <span>
                  ได้มาทั้งหมด{" "}
                  <span className="font-mono font-semibold text-green-600 dark:text-green-400">
                    {lifetime.earned.toLocaleString()}
                  </span>
                </span>
                <span>
                  ใช้ไป{" "}
                  <span className="font-mono font-semibold text-orange-600 dark:text-orange-400">
                    {lifetime.redeemed.toLocaleString()}
                  </span>
                </span>
                {lifetime.expired > 0 && (
                  <span>
                    หมดอายุ{" "}
                    <span className="font-mono font-semibold text-red-500 dark:text-red-400">
                      {lifetime.expired.toLocaleString()}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Small segmented tab control (in-box filter switcher). */
function SegTabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string; count?: number }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg bg-muted/60 p-0.5">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            active === t.id
              ? "bg-white text-foreground shadow-sm dark:bg-gray-700"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {t.label}
          {t.count != null && <span className="ml-1 opacity-60">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function LoyaltyTab({ customer, tier, program, storefrontKey }: LoyaltyTabProps) {
  const history = useCustomerPointHistory(customer, 200, 0, !!customer);
  const claims = useCustomerClaimedRewards(customer);
  const { data: orders } = useCustomerOrders(customer);
  const { data: config } = useRewardsConfig(storefrontKey);

  const entries = history.data?.entries ?? [];
  const balance = history.data?.current_balance ?? 0;
  const claimList = claims.data?.claims ?? [];

  // Derived loyalty insights: tier progress, lifetime totals, expiring points.
  const spend12m = spendLast12m((orders as Array<Record<string, any>>) ?? []);
  const progress = tierProgress(config?.tiers, tier, spend12m);
  const lifetime = lifetimeStats(entries);
  const expiring = expiringSoon(entries, balance);
  const pointsValue = balance * (config?.conversion_factor ?? 0);

  // Points box — earned (+) vs spent (−), paginated.
  const [pointsTab, setPointsTab] = useState<"earned" | "spent">("earned");
  const [page, setPage] = useState(0);
  const earned = entries.filter((e) => e.points > 0);
  const spent = entries.filter((e) => e.points < 0);
  const activeEntries = pointsTab === "earned" ? earned : spent;
  const total = activeEntries.length;
  const pageCount = Math.max(1, Math.ceil(total / HISTORY_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * HISTORY_PER_PAGE;
  const pageEntries = activeEntries.slice(start, start + HISTORY_PER_PAGE);
  const switchPointsTab = (t: "earned" | "spent") => {
    setPointsTab(t);
    setPage(0);
  };

  // Wallet box — unused (Claimed) vs used (Fulfilled/Cancelled).
  const [walletTab, setWalletTab] = useState<"active" | "used">("active");
  const activeClaims = claimList.filter((c) => c.status === "Claimed");
  const usedClaims = claimList.filter((c) => c.status !== "Claimed");
  const shownClaims = walletTab === "active" ? activeClaims : usedClaims;

  return (
    <div className="flex flex-col gap-6 xl:flex-row">
        {/* Main column — key info + action on top, then the two tabbed boxes. */}
        <div className="min-w-0 flex-1 space-y-6">
          <PointsSummaryCard
            customer={customer}
            balance={balance}
            tier={tier}
            program={program}
            progress={progress}
            lifetime={lifetime}
            pointsValue={pointsValue}
          />

          {/* Expiring-points nudge — best-effort from the nearest future expiry. */}
          {expiring && (
            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 dark:border-amber-900/40 dark:bg-amber-950/30">
              <Clock className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-[13px] text-amber-900 dark:text-amber-200">
                <span className="font-semibold">{expiring.points.toLocaleString()} แต้ม</span> จะหมดอายุ{" "}
                {timeAgo(expiring.date)}
              </div>
            </div>
          )}

          {/* Points box — title + get/spend tabs live INSIDE the card. */}
          <section>
            <div className={CARD}>
              <div className="p-5">
                <CardTitle icon={<Star className="h-3.5 w-3.5" />}>ประวัติแต้ม</CardTitle>
                <div className="mb-4">
                  <SegTabs
                    active={pointsTab}
                    onChange={switchPointsTab}
                    tabs={[
                      { id: "earned", label: "ได้แต้ม", count: earned.length },
                      { id: "spent", label: "ใช้แต้ม", count: spent.length },
                    ]}
                  />
                </div>

                {history.isLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 rounded-lg" />
                    ))}
                  </div>
                ) : total === 0 ? (
                  <div className="py-10 text-center text-[14px] text-muted-foreground">
                    {pointsTab === "earned" ? "ยังไม่มีการได้แต้ม" : "ยังไม่มีการใช้แต้ม"}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {groupEntriesByDay(pageEntries).map(([day, rows]) => (
                      <div key={day}>
                        <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                          {day}
                        </div>
                        <div>
                          {rows.map((e, i) => (
                            <PointTimelineRow
                              key={e.name}
                              entry={e}
                              isFirst={i === 0}
                              isLast={i === rows.length - 1}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {!history.isLoading && total > HISTORY_PER_PAGE && (
                    <div className="flex items-center justify-between border-t border-border/40 px-5 py-2.5">
                      <span className="text-[11px] text-muted-foreground">
                        {start + 1}–{Math.min(start + HISTORY_PER_PAGE, total)} จาก {total}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                          disabled={safePage === 0}
                          aria-label="ก่อนหน้า"
                          className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                          disabled={safePage >= pageCount - 1}
                          aria-label="ถัดไป"
                          className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
              )}
            </div>
          </section>
        </div>

        {/* Sidebar — member card + the reward wallet box. */}
        <div className="order-first w-full shrink-0 space-y-4 xl:order-last xl:w-[360px]">
          <MemberCardForTier tier={tier} storefrontKey={storefrontKey} mini />

          {/* Items box — title + used-state tabs live INSIDE the card. */}
          <section>
            <div className={CARD}>
              <div className="p-5">
                <CardTitle icon={<Gift className="h-3.5 w-3.5" />}>กระเป๋ารางวัล</CardTitle>
                <div className="mb-4">
                  <SegTabs
                    active={walletTab}
                    onChange={setWalletTab}
                    tabs={[
                      { id: "active", label: "พร้อมใช้", count: activeClaims.length },
                      { id: "used", label: "ใช้แล้ว", count: usedClaims.length },
                    ]}
                  />
                </div>

                {claims.isLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 2 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 rounded-lg" />
                    ))}
                  </div>
                ) : shownClaims.length === 0 ? (
                  <div className="py-10 text-center text-[14px] text-muted-foreground">
                    {walletTab === "active" ? "ไม่มีรางวัลที่พร้อมใช้" : "ยังไม่มีรางวัลที่ใช้แล้ว"}
                  </div>
                ) : (
                  <div className="-my-3">
                    {shownClaims.map((c, i) => (
                      <Fragment key={c.name}>
                        {i > 0 && <div className="border-t border-border/40" />}
                        <ClaimRow claim={c} />
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
  );
}
